import React, { useMemo } from "react";
import { Scissors, Sparkles } from "lucide-react";
import { GptAnswer } from "./GptAnswer";
import { useGptJob } from "./useGptJob";

const eur = (n) => `€${Math.round(n).toLocaleString("fr-FR")}`;
const AI_THEMES = ["AI_INFRA", "AI_SOFT"];

export const TrimAnalysis = ({ owner, rows, total, exposure, cap, aiLimit }) => {
  const [gpt, runGpt] = useGptJob(owner);

  const stats = useMemo(() => {
    const over = rows.filter((r) => r.weight_pct > cap).map((r) => ({
      ...r, sell: r.total_value - (total * cap) / 100,
    }));
    const watch = rows.filter((r) => r.weight_pct > 10 && r.weight_pct <= cap);
    const top2 = rows.slice(0, 2).reduce((a, r) => a + r.weight_pct, 0);
    const ai = AI_THEMES.reduce((a, t) => a + (exposure?.by_theme?.[t]?.pct || 0), 0);
    return { over, watch, top2, ai };
  }, [rows, total, exposure, cap]);

  const Kpi = ({ label, value, tone, testid }) => (
    <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3" data-testid={testid}>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className={`text-lg font-bold ${tone}`}>{value}</div>
    </div>
  );

  return (
    <div className="bg-slate-800/50 border border-orange-500/30 rounded-lg p-4" data-testid="pe-trim">
      <div className="font-semibold text-slate-100 mb-3 flex items-center gap-2">
        <Scissors className="w-5 h-5 text-orange-400" /> Analyse des allègements
        <span className="text-[11px] text-slate-500 font-normal">plafond {cap}% par action</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Kpi label={`Exposition IA / semis (limite ${aiLimit}%)`} value={`${stats.ai.toFixed(1)}%`} tone={stats.ai > aiLimit ? "text-red-400" : "text-emerald-400"} testid="pe-trim-ai" />
        <Kpi label="Top 2 lignes cumulées" value={`${stats.top2.toFixed(1)}%`} tone={stats.top2 > 30 ? "text-amber-400" : "text-slate-100"} testid="pe-trim-top2" />
        <Kpi label="Si l'IA/semis baisse de 30 %" value={`-${eur((total * stats.ai * 0.3) / 100)}`} tone="text-red-400" testid="pe-trim-stress" />
        <Kpi label="Lignes au-dessus du plafond" value={stats.over.length} tone={stats.over.length ? "text-red-400" : "text-emerald-400"} testid="pe-trim-over-count" />
      </div>
      {stats.over.map((r) => (
        <div key={r.ticker} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm bg-red-500/10 border border-red-500/30 rounded px-3 py-2 mb-2" data-testid={`pe-trim-over-${r.ticker}`}>
          <span className="font-bold text-slate-100 w-20">{r.ticker}</span>
          <span className="text-red-400 font-mono">{r.weight_pct.toFixed(1)}%</span>
          <span className="text-slate-300">Vendre ~<b className="text-orange-300">{eur(r.sell)}</b> pour revenir à {cap}%</span>
          <span className="text-slate-500 text-xs">-30 % sur la ligne = -{((r.weight_pct * 0.3)).toFixed(1)} pt de portefeuille</span>
        </div>
      ))}
      {stats.watch.map((r) => (
        <div key={r.ticker} className="flex items-center gap-4 text-sm bg-amber-500/5 border border-amber-500/20 rounded px-3 py-2 mb-2" data-testid={`pe-trim-watch-${r.ticker}`}>
          <span className="font-bold text-slate-100 w-20">{r.ticker}</span>
          <span className="text-amber-400 font-mono">{r.weight_pct.toFixed(1)}%</span>
          <span className="text-slate-400">Sous le plafond : ne pas renforcer</span>
        </div>
      ))}
      <button onClick={() => runGpt({ mode: "trim", cap_pct: cap, ai_limit_pct: aiLimit })} disabled={gpt?.loading}
        className="mt-2 inline-flex items-center gap-2 bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50 text-white rounded-full px-4 py-2 text-sm font-semibold transition-colors"
        data-testid="pe-trim-gpt-btn">
        <Sparkles className="w-4 h-4" /> Demander à GPT : alléger ou garder ?
      </button>
      <GptAnswer state={gpt} testid="pe-trim-gpt" />
    </div>
  );
};
